import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { WritableDraft } from 'immer';
import { compact } from 'lodash-es';
import { getScaleFromIncrement } from '../../../../fields';
import {
  SecurityTypesUtils,
  type Currency,
  type CustomerBalanceTransaction,
  type CustomerMarketAccount,
  type CustomerOrder,
  type CustomerQuote,
  type QuoteRequest,
} from '../../../../types';
import { isSpot } from '../../../../utils';
import type { SwapNewOrderSingle } from '../../OrderService';
import type { OrderFormDependencies } from '../OrderSlice/types';
import { getSwapInitialState } from './initialState';
import { getCurrenciesIncrement } from './selector';
import type { SwapState } from './types';
import { validateFromQty, validateQuantities, validateToQty } from './validation';

const swapSlice = createSlice({
  name: 'swap',
  initialState: getSwapInitialState(),
  reducers: {
    setReferenceData: (state, action: PayloadAction<Partial<SwapState['referenceData']>>) => {
      if (action.payload.currencies) {
        state.referenceData.currencies = action.payload.currencies;
      }
      if (action.payload.marketAccounts) {
        state.referenceData.marketAccounts = action.payload.marketAccounts;
        state.form.marketAccount = state.form.marketAccount.updateAvailableItems(
          action.payload.marketAccounts.customerMarketAccountsList
        );
      }
      if (action.payload.securities) {
        state.referenceData.securities = action.payload.securities;
      }
      if (action.payload.favoredPairCurrency) {
        state.referenceData.favoredPairCurrency = action.payload.favoredPairCurrency;
      }
      if (action.payload.homeCurrency) {
        state.referenceData.homeCurrency = action.payload.homeCurrency;
      }
    },
    primeForm: (
      state,
      action: PayloadAction<{
        toCurrency: string;
        fromCurrency?: string;
        marketAccountID?: CustomerMarketAccount['MarketAccountID'];
        toQty?: string;
        fromQty?: string;
      }>
    ) => {
      state = cleanForm(state);
      state.mode = 'swap';
      const { toCurrency, marketAccountID, fromCurrency, toQty, fromQty } = action.payload;

      const currencyObj = state.referenceData.currencies.currenciesBySymbol.get(toCurrency);
      state.form.toCurrency = state.form.toCurrency.updateValue(currencyObj);
      state.form.fromCurrency = state.form.fromCurrency.updateValue(undefined);
      const { favoredPairCurrency } = state.referenceData;
      updateAvailableCurrencies(state);
      const availableFromCurrenciesSet = new Set(state.form.fromCurrency.availableItems.map(c => c.Symbol));
      const suggestedFromCurrency =
        fromCurrency && availableFromCurrenciesSet.has(fromCurrency)
          ? fromCurrency
          : // if the favored pair currency is available, use it
            favoredPairCurrency?.Symbol && availableFromCurrenciesSet.has(favoredPairCurrency.Symbol)
            ? favoredPairCurrency.Symbol
            : // if the home currency is available, and not equal to the toCurrency, use it
              state.referenceData.homeCurrency &&
                state.referenceData.homeCurrency.Symbol !== toCurrency &&
                availableFromCurrenciesSet.has(state.referenceData.homeCurrency.Symbol)
              ? state.referenceData.homeCurrency.Symbol
              : state.form.fromCurrency.availableItems.at(0)?.Symbol;
      // we will find a currency that is not the same as the toCurrency, if not, the env is completely broken.
      if (suggestedFromCurrency) {
        const fromCurrencyObject = state.referenceData.currencies.currenciesBySymbol.get(suggestedFromCurrency);
        state.form.fromCurrency = state.form.fromCurrency.updateValue(fromCurrencyObject);
      }
      updateAvailableCurrencies(state);
      updateScales(state);

      if (toQty) {
        state.form.toQty = state.form.toQty.updateValue(toQty, true);
        state.meta.lastTouched = 'toQty';
      }
      if (fromQty) {
        state.form.fromQty = state.form.fromQty.updateValue(fromQty, true);
        state.meta.lastTouched = 'fromQty';
      }

      const marketAccountToSelect = marketAccountID ?? state.form.marketAccount.availableItems.at(0)?.MarketAccountID;
      state.form.marketAccount = state.form.marketAccount.updateValueFromID(marketAccountToSelect);
    },
    setToCurrency: (state, action: PayloadAction<Currency | undefined>) => {
      if (action.payload?.Symbol === state.form.fromCurrency.value?.Symbol) {
        switchCurrencies(state);
        state.meta.lastTouched = 'toQty';
      } else {
        state.form.toCurrency = state.form.toCurrency.updateValue(action.payload);
        state.meta.lastTouched = 'fromQty';
        // set the currency and scale etc
        state.form.toQty = state.form.toQty.updateValue('').setTouched(false);
        updateScales(state);
      }
      validateToQty(state);
      updateAvailableCurrencies(state);
    },
    setFromCurrency: (state, action: PayloadAction<Currency | undefined>) => {
      if (action.payload?.Symbol === state.form.toCurrency.value?.Symbol) {
        switchCurrencies(state);
        state.meta.lastTouched = 'fromQty';
      } else {
        state.form.fromCurrency = state.form.fromCurrency.updateValue(action.payload);
        state.meta.lastTouched = 'toQty';
        // set the currency and scale etc
        state.form.fromQty = state.form.fromQty.updateValue('').setTouched(false);
        updateScales(state);
      }
      validateFromQty(state);
      updateAvailableCurrencies(state);
    },
    setToQuantity: (state, action: PayloadAction<{ qty: string; setTouched?: boolean }>) => {
      state.form.toQty = state.form.toQty.updateValue(action.payload.qty, !action.payload.setTouched);

      if (action.payload.setTouched) {
        state.meta.lastTouched = 'toQty';
      }
      validateQuantities(state);
    },
    setFromQuantity: (state, action: PayloadAction<{ qty: string; setTouched?: boolean }>) => {
      state.form.fromQty = state.form.fromQty.updateValue(action.payload.qty, !action.payload.setTouched);

      if (action.payload.setTouched) {
        state.meta.lastTouched = 'fromQty';
      }
      validateQuantities(state);
    },
    setMarketAccount: (state, action: PayloadAction<CustomerMarketAccount | undefined>) => {
      state.form.marketAccount = state.form.marketAccount.updateValue(action.payload);
    },
    switchSides: state => {
      const { fromCurrency, fromQty, toCurrency, toQty } = state.form;
      state.form.fromCurrency = toCurrency;
      state.form.fromQty = toQty;

      state.form.toCurrency = fromCurrency;
      state.form.toQty = fromQty;

      state.meta.lastTouched = state.meta.lastTouched === 'fromQty' ? 'toQty' : 'fromQty';

      validateQuantities(state);
    },
    setQuote: (state, action: PayloadAction<{ quote: CustomerQuote | undefined; lock: boolean } | undefined>) => {
      if (!action.payload?.quote) {
        state.quote = undefined;
        state.meta.step = 'idle';
        return;
      }

      state.quote = action.payload?.quote;
      if (action.payload?.lock) {
        state.meta.step = 'locked';
      }
      validateQuantities(state);
    },
    setQuoteRequest: (state, action: PayloadAction<QuoteRequest | undefined>) => {
      state.quoteRequest = action.payload;
    },
    setSentOrderPayload: (state, action: PayloadAction<SwapNewOrderSingle | undefined>) => {
      state.meta.sentOrderPayload = action.payload;
      if (action.payload) {
        state.meta.step = 'sendingEntity';
      }
    },

    initialiseTopup: state => {
      cleanForm(state);
      state.mode = 'topup';
      const { homeCurrency } = state.referenceData;
      state.form.fromCurrency = state.form.fromCurrency.updateValue(homeCurrency);
      state.form.fromQty = state.form.fromQty.updateValue('', true);
    },

    initialiseWithdrawal: state => {
      cleanForm(state);
      state.mode = 'withdraw';
      const { homeCurrency } = state.referenceData;
      state.form.fromCurrency = state.form.fromCurrency.updateValue(homeCurrency);
    },

    setDependencies: (state, action: PayloadAction<Partial<OrderFormDependencies>>) => {
      const prevDependencies = state.dependencies;
      state.dependencies = { ...prevDependencies, ...action.payload };
      // Need to validate quantity again since it depends on dependencies.
      validateQuantities(state);
    },
    setSymbol: (state, action: PayloadAction<string | undefined>) => {
      const symbol = action.payload;
      if (symbol) {
        const security = state.referenceData.securities.securitiesBySymbol.get(symbol);
        if (security && SecurityTypesUtils.Spot.isType(security)) {
          const fromCurrency = state.referenceData.currencies.currenciesBySymbol.get(security.QuoteCurrency);
          const toCurrency = state.referenceData.currencies.currenciesBySymbol.get(security.BaseCurrency);
          if (fromCurrency && toCurrency) {
            state.form.fromCurrency = state.form.fromCurrency.updateValue(fromCurrency);
            state.form.toCurrency = state.form.toCurrency.updateValue(toCurrency);

            updateScales(state);

            // Reset quantities
            state.form.fromQty = state.form.fromQty.updateValue('', true);
            state.form.toQty = state.form.toQty.updateValue('', true);
          }
        }
      }
      updateAvailableCurrencies(state);
    },

    resetForm: (state, action: PayloadAction<{ keepFromQty?: boolean }>) => {
      const { keepFromQty } = action.payload;
      const oldFromQty = state.form.fromQty;
      cleanForm(state);
      if (keepFromQty) {
        state.form.fromQty = oldFromQty;
        state.meta.lastTouched = 'fromQty';
      }
      updateAvailableCurrencies(state);
    },
    setStep: (state, action: PayloadAction<SwapState['meta']['step']>) => {
      state.meta.step = action.payload;
    },
    setRequestStep: (state, action: PayloadAction<SwapState['requestStep']>) => {
      state.requestStep = action.payload;
    },
    setTakingLongerThanExpected: (state, action: PayloadAction<boolean>) => {
      state.meta.takingLongerThanExpected = action.payload;
    },
    setFocusedOrder: (state, action: PayloadAction<CustomerOrder | undefined>) => {
      state.meta.order = action.payload;
    },
    setTransactionID: (state, action: PayloadAction<string | undefined>) => {
      state.meta.transactionID = action.payload;
    },
    setTransaction: (state, action: PayloadAction<CustomerBalanceTransaction | undefined>) => {
      state.meta.balanceTransaction = action.payload;
    },
    setQuoteCountdown: (state, action: PayloadAction<number | undefined>) => {
      if (action.payload === undefined) {
        state.meta.quoteCountdown = undefined;
      } else {
        state.meta.quoteCountdown = action.payload;
      }
    },
  },
});

const cleanForm = (state: WritableDraft<SwapState>) => {
  const oldForm = state.form;
  const oldReferenceData = state.referenceData;
  const previousMode = state.mode;

  state.referenceData = oldReferenceData;
  state.form.fromCurrency = oldForm.fromCurrency;
  state.form.toCurrency = oldForm.toCurrency;
  state.form.fromQty = oldForm.fromQty.updateValue('', true);
  state.form.toQty = oldForm.toQty.updateValue('', true);

  const newState = getSwapInitialState();
  state.meta = newState.meta;
  state.quote = newState.quote;
  state.quoteRequest = newState.quoteRequest;
  state.mode = previousMode;
  state.requestStep = 'resting';
  state.meta.takingLongerThanExpected = false;
  return state;
};

export const {
  setReferenceData: setSwapReferenceData,
  setFromCurrency: setSwapFromCurrency,
  setFromQuantity: setSwapFromQuantity,
  setToCurrency: setSwapToCurrency,
  setMarketAccount: setSwapMarketAccount,
  setToQuantity: setSwapToQuantity,
  switchSides: switchSwapSides,
  setQuote: setSwapQuote,
  setQuoteRequest: setSwapQuoteRequest,
  resetForm: resetSwapForm,
  setStep: setSwapStep,
  setSentOrderPayload: setSwapSentOrderPayload,
  setTransaction: setSwapTransaction,
  setFocusedOrder: setSwapFocusedOrder,
  setTransactionID: setSwapTransactionID,
  setDependencies: setSwapDependencies,
  primeForm: primeSwapForm,
  setQuoteCountdown: setSwapQuoteCountdown,
  setSymbol: setSwapSymbol,
  initialiseTopup: initialiseSwapTopup,
  initialiseWithdrawal: initialiseSwapWithdrawal,
  setRequestStep: setSwapRequestStep,
  setTakingLongerThanExpected: setSwapTakingLongerThanExpected,
} = swapSlice.actions;

const switchCurrencies = (state: WritableDraft<SwapState>) => {
  const temp = state.form.fromCurrency.updateValue(state.form.fromCurrency.value);
  state.form.fromCurrency = state.form.toCurrency;
  state.form.toCurrency = temp;
  const tempQty = state.form.fromQty.updateValue(state.form.fromQty.value);
  state.form.fromQty = state.form.toQty;
  state.form.toQty = tempQty;
};

const updateScales = (state: WritableDraft<SwapState>) => {
  const { fromCurrencyDetails, toCurrencyDetails } = getCurrenciesIncrement(state);
  if (fromCurrencyDetails) {
    state.form.fromQty = state.form.fromQty.updateScale(getScaleFromIncrement(fromCurrencyDetails.minIncrement));
  }

  if (toCurrencyDetails) {
    state.form.toQty = state.form.toQty.updateScale(getScaleFromIncrement(toCurrencyDetails.minIncrement));
  }
};

// TODO(robertz): possibly improve this to store per currency available items, but for now this is fine.
function updateAvailableCurrencies(state: WritableDraft<SwapState>) {
  const securitiesList = state.referenceData.securities.securitiesList?.filter(isSpot);
  if (securitiesList != null) {
    // Get all unique currencies from securities
    const allCurrencies = new Set<string>();
    securitiesList.forEach(security => {
      if (SecurityTypesUtils.Spot.isType(security)) {
        allCurrencies.add(security.BaseCurrency);
        allCurrencies.add(security.QuoteCurrency);
      }
    });

    // Filter available currencies for fromCurrency based on what can pair with toCurrency
    const availableFromCurrencies = Array.from(allCurrencies).filter(fromCurrency => {
      if (state.form.toCurrency.value) {
        // Check if there's a security that pairs fromCurrency with toCurrency
        return securitiesList.some(
          security =>
            (security.BaseCurrency === fromCurrency &&
              security.QuoteCurrency === state.form.toCurrency.value?.Symbol) ||
            (security.BaseCurrency === state.form.toCurrency.value?.Symbol && security.QuoteCurrency === fromCurrency)
        );
      }
      return true; // If no toCurrency selected, all currencies are available
    });

    // Filter available currencies for toCurrency based on what can pair with fromCurrency
    const availableToCurrencies = Array.from(allCurrencies).filter(toCurrency => {
      if (state.form.fromCurrency.value) {
        // Check if there's a security that pairs fromCurrency with toCurrency
        return securitiesList.some(
          security =>
            (security.BaseCurrency === state.form.fromCurrency.value?.Symbol &&
              security.QuoteCurrency === toCurrency) ||
            (security.BaseCurrency === toCurrency && security.QuoteCurrency === state.form.fromCurrency.value?.Symbol)
        );
      }
      return true; // If no fromCurrency selected, all currencies are available
    });

    const fromCurrencies = compact(
      availableFromCurrencies.map(c => state.referenceData.currencies.currenciesBySymbol.get(c))
    );
    const toCurrencies = compact(
      availableToCurrencies.map(c => state.referenceData.currencies.currenciesBySymbol.get(c))
    );

    // Update the available items for both currency selectors
    state.form.fromCurrency = state.form.fromCurrency
      .updateAvailableItems(fromCurrencies)
      .setDisabled(fromCurrencies.length === 1);
    state.form.toCurrency = state.form.toCurrency
      .updateAvailableItems(toCurrencies)
      .setDisabled(toCurrencies.length === 1);
  }
}

export const swapSliceReducer = swapSlice.reducer;
